import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import API from './API';
import { env } from './env';
import { authSessionEvents } from './helpers/authSessionEvents';
import { clearAuthFromStorage } from './helpers/authStorage';
import { getDeviceHash } from './helpers/deviceHash';
import LS from './localStorageUtils';

type RetryableAxiosRequestConfig = AxiosRequestConfig & {
	_retry?: boolean;
};

let isRefreshing = false;
let failedQueue: Array<{
	resolve: (token: string) => void;
	reject: (error: any) => void;
}> = [];

const getCurrentLanguage = () => LS.get('lang') || 'en';

const processQueue = (error: any, token: string | null = null) => {
	failedQueue.forEach(promise => {
		if (error) {
			promise.reject(error);
		} else if (token) {
			promise.resolve(token);
		}
	});

	failedQueue = [];
};

const handleRefreshFailure = (error: any) => {
	// If the refresh endpoint returned a server response (any status), the session is dead
	// — the token was rejected definitively. Network errors (no response) are transient and
	// don't force-logout so the user can retry.
	if (!error?.response) {
		return;
	}

	clearAuthFromStorage();
	authSessionEvents.emitInvalidated({
		reason: 'refresh_failed',
		message: error?.response?.data?.message,
	});
};

export const requestNewAccessToken = async (): Promise<string> => {
	const refresh_token = LS.get('refresh_token');

	if (!refresh_token) {
		throw new Error('No refresh token found');
	}

	const response = await API.authRequest(
		'/refresh_token',
		'post',
		{ refresh_token },
		{
			'Content-Type': 'application/json',
			...API.getXCasinoHeader(),
			...API.getBearerHeader(),
		},
	);

	if (response.Error) {
		throw response.Error;
	}

	const data = response.Data?.data || response.Data;

	const newAccessToken = data?.access_token;
	const newRefreshToken = data?.refresh_token;

	if (!newAccessToken) {
		throw new Error('No access token received from refresh endpoint');
	}

	LS.set('access_token', newAccessToken);

	if (newRefreshToken) {
		LS.set('refresh_token', newRefreshToken);
	}

	const storedUser = LS.get('auth_user');
	if (storedUser) {
		try {
			const parsed = JSON.parse(storedUser);
			LS.set(
				'auth_user',
				JSON.stringify({
					...parsed,
					accessToken: newAccessToken,
					refreshToken: newRefreshToken ?? parsed.refreshToken,
				}),
			);
		} catch (e) {
			console.warn('[refresh] Failed to update auth_user in storage', e);
		}
	}

	return newAccessToken;
};

const attachAuthHeaderInterceptor = (
	client: ReturnType<typeof axios.create>,
	options?: {
		withCasinoContext?: boolean;
		withCasinoHeader?: boolean;
	},
) => {
	client.interceptors.request.use(config => {
		config.headers = config.headers ?? {};

		const token = LS.get('access_token');
		const casinoId = env('CASINO_ID');

		if (token) {
			(config.headers as any).Authorization = `Bearer ${token}`;
		}

		if (options?.withCasinoContext) {
			(config.headers as any)['X-Casino-Context'] = casinoId;
		}

		if (options?.withCasinoHeader) {
			Object.assign(config.headers, API.getXCasinoHeader());
		}

		(config.headers as any)['Accept-Language'] = getCurrentLanguage();
		(config.headers as any)['player-device-hash'] = getDeviceHash();

		return config;
	});
};

const attachRefreshInterceptor = (client: ReturnType<typeof axios.create>) => {
	client.interceptors.response.use(
		(response: AxiosResponse) => response,
		async (error: AxiosError) => {
			const originalRequest = error.config as RetryableAxiosRequestConfig;
			const hasRefreshToken = !!LS.get('refresh_token');
			const hasAuthorizationHeader = !!(originalRequest?.headers as any)?.Authorization;

			if (!error.response || error.response.status !== 401 || !originalRequest) {
				return Promise.reject(error);
			}

			if (originalRequest.url?.includes('/refresh_token')) {
				return Promise.reject(error);
			}

			if (!hasRefreshToken || !hasAuthorizationHeader) {
				return Promise.reject(error);
			}

			if (originalRequest._retry) {
				return Promise.reject(error);
			}

			originalRequest._retry = true;

			if (isRefreshing) {
				return new Promise((resolve, reject) => {
					failedQueue.push({
						resolve: (token: string) => {
							originalRequest.headers = originalRequest.headers ?? {};
							(originalRequest.headers as any).Authorization = `Bearer ${token}`;
							resolve(client(originalRequest));
						},
						reject,
					});
				});
			}

			isRefreshing = true;

			try {
				const newToken = await requestNewAccessToken();

				processQueue(null, newToken);

				originalRequest.headers = originalRequest.headers ?? {};
				(originalRequest.headers as any).Authorization = `Bearer ${newToken}`;

				return client(originalRequest);
			} catch (refreshError) {
				handleRefreshFailure(refreshError);
				processQueue(refreshError, null);
				return Promise.reject(refreshError);
			} finally {
				isRefreshing = false;
			}
		},
	);
};

export const axiosInstance = axios.create({
	baseURL: env('SVARA_API_URL'),
	timeout: 10000,
});

export const bowaApi = axios.create({
	baseURL: env('PLATFORM_API_URL'),
});

export const svaraApi = axios.create({
	baseURL: env('SVARA_API_URL'),
});

export const casinoApi = axios.create({
	baseURL: env('SVARA_API_URL'),
});

// request interceptors
attachAuthHeaderInterceptor(axiosInstance, {
	withCasinoHeader: false,
	withCasinoContext: false,
});

attachAuthHeaderInterceptor(svaraApi, {
	withCasinoHeader: true,
	withCasinoContext: true,
});

attachAuthHeaderInterceptor(bowaApi, {
	withCasinoHeader: true,
	withCasinoContext: false,
});

casinoApi.interceptors.request.use(config => {
	const casinoId = env('CASINO_ID');

	config.headers = config.headers ?? {};
	(config.headers as any)['X-Casino-Context'] = casinoId;
	(config.headers as any)['Accept-Language'] = getCurrentLanguage();
	(config.headers as any)['player-device-hash'] = getDeviceHash();
	Object.assign(config.headers, API.getXCasinoHeader());

	return config;
});

// response refresh interceptors
attachRefreshInterceptor(axiosInstance);
attachRefreshInterceptor(svaraApi);
attachRefreshInterceptor(bowaApi);
attachRefreshInterceptor(casinoApi);

const UPLOAD_ATTEMPTS = 3;
const UPLOAD_RETRY_DELAY_MS = 400;

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const isNoResponseNetworkError = (err: any) => !err?.response && err?.code !== 'ECONNABORTED';

export const uploadFile = async (file: File): Promise<string> => {
	let lastError: any;

	for (let attempt = 1; attempt <= UPLOAD_ATTEMPTS; attempt++) {
		const formData = new FormData();
		formData.append('file', file);

		try {
			const res = await svaraApi.post('/api/file-storage', formData, {
				headers: {
					'Content-Type': 'multipart/form-data',
					'X-Trinity-Update-Remarks': 'remarks',
				},
			});

			const fileId = res?.data?.fileId ?? res?.data?.id ?? res?.data?.data?.id;
			if (!fileId) {
				throw new Error('Upload succeeded but no fileId returned');
			}

			if (attempt > 1) {
				console.log(`[upload] succeeded on attempt ${attempt}`);
			}

			return String(fileId);
		} catch (err: any) {
			lastError = err;

			if (!isNoResponseNetworkError(err) || attempt === UPLOAD_ATTEMPTS) {
				throw err;
			}

			console.warn(
				`[upload] attempt ${attempt}/${UPLOAD_ATTEMPTS} failed with ${err?.code}, retrying`,
			);
			await wait(UPLOAD_RETRY_DELAY_MS * attempt);
		}
	}

	throw lastError;
};

export const getFileStorageUrl = (fileId: string): string => {
	return `${env('SVARA_API_URL')}/api/file-storage/${fileId}`;
};

export const getAvatarUrl = (fileId: string): string => {
	return getFileStorageUrl(fileId);
};
